import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ProfileCompletionInput } from "@colab/shared";
import type { PortalProfile } from "./use-profile";

type Context = {
  previous: PortalProfile | undefined;
  key: ReturnType<typeof queryKeys.portal.profile>;
};

// PATCH /api/portal/profile
//
// optimistic: profile-completion form patches the changed fields in place;
// the next refetch reconciles trimmed strings and the server-set
// onboarded_at timestamp. Replaces revalidatePath("/portal/profile") +
// revalidatePath("/portal/dashboard") from
// /app/portal/_actions.ts::partnerUpdateProfile.
//
// Pattern B (field-level) — see MIGRATION.md "Optimistic-update reference".
export function useUpdatePortalProfile() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ProfileCompletionInput, Context>({
    mutationFn: (patch) =>
      apiFetch<{ ok: true }>("/portal/profile", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onMutate: async (patch) => {
      const key = queryKeys.portal.profile();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PortalProfile>(key);
      queryClient.setQueryData<PortalProfile>(key, (old) =>
        old
          ? {
              ...old,
              full_name: patch.full_name,
              role_at_partner: patch.role_at_partner,
              website: patch.website ?? old.website,
              integrates_with: patch.integrates_with ?? old.integrates_with,
            }
          : old,
      );
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to save profile");
    },
    onSettled: () => {
      // Server is source of truth — refetch reconciles trimmed strings,
      // the onboarded_at stamp, and any normalization.
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.profile() });
      // Also invalidate /auth/me — full_name updates ripple to SessionDTO
      // so the top-nav avatar/name picks up the change immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
  });
}
